import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { ICategory } from '../../types/damTypes';
import { API_BASE_URL } from '../../config';
import axiosInstance from '../../services/axiosInstance';
import { 
    Box, 
    Typography, 
    List, 
    ListItem, 
    ListItemText, 
    IconButton, 
    CircularProgress, 
    Button, 
    Collapse,
    TextField,
    Chip,
    Stack,
    Paper,
    Divider,
    Tooltip,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    FormControlLabel,
    Switch
} from '@mui/material';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import CategoryIcon from '@mui/icons-material/Category';

interface CategoryManagementPageProps {
    categories: ICategory[];
    loading: boolean;
    error: string | null;
    onCategoryUpdated: () => Promise<void>;
}

interface CategoryBrowseSettings {
    hideEmptyCategoriesEnabled: boolean;
    updatedAt: string | null;
}

// Helper to notify App.tsx that categories have been updated
const notifyCategoriesUpdated = () => {
    window.dispatchEvent(new CustomEvent('dam:categories-updated'));
};

// Fördefinierade kategorimallar för vanliga produktkategorier
const CATEGORY_TEMPLATES = {
    produkter: ['Mejeri', 'Charkuteri', 'Kött', 'Fisk', 'Frukt', 'Grönsaker', 'Bröd', 'Konserver', 'Kryddor', 'Drycker'],
    mat: ['Frukost', 'Lunch', 'Middag', 'Mellanmål', 'Desert', 'Bakning'],
    butik: ['Skyltmaterial', 'Prisskiltar', 'Kampanjmaterial', 'Broschyrer', 'Menyer'],
    media: ['Bilder', 'Videor', 'Illustrationer', 'Logotyper', 'Ikoner'],
    social: ['Facebook', 'Instagram', 'Twitter', 'LinkedIn', 'TikTok', 'YouTube']
};

// Isolated ChipInput component defined outside to prevent re-creation
interface ChipInputProps {
    chips: string[];
    onChipsChange: (chips: string[]) => void;
    placeholder?: string;
}

const IsolatedChipInput: React.FC<ChipInputProps> = ({ chips, onChipsChange, placeholder = "Skriv kategorinamn och tryck Enter..." }) => {
    const [inputValue, setInputValue] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && inputValue.trim()) {
            e.preventDefault();
            const newChip = inputValue.trim();
            if (!chips.includes(newChip)) {
                onChipsChange([...chips, newChip]);
            }
            setInputValue('');
            // Keep focus
            inputRef.current?.focus();
        } else if (e.key === 'Backspace' && !inputValue && chips.length > 0) {
            e.preventDefault();
            onChipsChange(chips.slice(0, -1));
        }
    };

    const handleRemoveChip = (index: number) => {
        onChipsChange(chips.filter((_, i) => i !== index));
    };

    const clearAll = () => {
        onChipsChange([]);
        inputRef.current?.focus();
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 0.5,
                minHeight: '60px',
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                backgroundColor: '#fff',
                '&:focus-within': {
                    borderColor: '#1976d2',
                    borderWidth: '2px',
                    padding: '7px 11px'
                }
            }}
            onClick={() => inputRef.current?.focus()}
        >
            {chips.map((chip, index) => (
                <Chip
                    key={index}
                    label={chip}
                    onDelete={() => handleRemoveChip(index)}
                    size="small"
                    sx={{
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2'
                    }}
                />
            ))}
            <input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={chips.length === 0 ? placeholder : "Lägg till fler..."}
                style={{
                    border: 'none',
                    outline: 'none',
                    fontSize: '14px',
                    padding: '4px 0',
                    flex: 1,
                    minWidth: '200px',
                    fontFamily: 'inherit',
                    backgroundColor: 'transparent'
                }}
                autoComplete="off"
                spellCheck={false}
            />
            {chips.length > 0 && (
                <IconButton 
                    size="small" 
                    onClick={clearAll}
                    sx={{ color: '#666' }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            )}
        </Box>
    );
};

interface SortableItemProps {
    id: string;
    children: React.ReactNode;
}

const SortableMainItem: React.FC<SortableItemProps> = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style: React.CSSProperties = {
        transform: CSS.Translate.toString(transform),
        transition,
        zIndex: isDragging ? 1 : undefined,
        opacity: isDragging ? 0.9 : 1,
    };
    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            {children}
        </div>
    );
};

const SortableSubItem: React.FC<SortableItemProps> = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style: React.CSSProperties = {
        transform: CSS.Translate.toString(transform),
        transition,
        zIndex: isDragging ? 1 : undefined,
        opacity: isDragging ? 0.9 : 1,
    };
    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            {children}
        </div>
    );
};

interface CategoryListItemProps {
    category: ICategory;
    getSubcategories: (parentId: string) => ICategory[];
    expandedCategories: Record<string, boolean>;
    editingCategory: string | null;
    editName: string;
    savingEdit: boolean;
    deletingCategoryId: string | null;
    mainCategories: ICategory[];
    sensors: any;
    setBulkAddOpen: React.Dispatch<React.SetStateAction<string | null>>;
    toggleExpand: (categoryId: string) => void;
    startEdit: (category: ICategory) => void;
    setEditName: React.Dispatch<React.SetStateAction<string>>;
    saveEdit: () => void;
    cancelEdit: () => void;
    handleDeleteCategory: (categoryId: string, categoryName: string) => void;
    handleCategoryUpdated: () => Promise<void>;
    reorderWithinParent: (parentId: string | null, orderedIds: string[]) => Promise<void>;
}

const CategoryListItem: React.FC<CategoryListItemProps> = ({
    category,
    getSubcategories,
    expandedCategories,
    editingCategory,
    editName,
    savingEdit,
    deletingCategoryId,
    mainCategories,
    sensors,
    setBulkAddOpen,
    toggleExpand,
    startEdit,
    setEditName,
    saveEdit,
    cancelEdit,
    handleDeleteCategory,
    handleCategoryUpdated,
    reorderWithinParent,
}) => {
    const subcategories = getSubcategories(category._id)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name));
    const hasSubcategories = subcategories.length > 0;
    const isExpanded = expandedCategories[category._id] || false;
    const isEditing = editingCategory === category._id;
    const isMainCategory = !category.parentCategory;

    const content = (
        <Paper 
            elevation={0} 
            sx={{ 
                mb: 1,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                backgroundColor: isMainCategory ? '#f5f5f5' : '#fff'
            }}
        >
            <ListItem
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    pl: category.parentCategory ? 4 : 2,
                    py: 1.5,
                    backgroundColor: isMainCategory ? '#f5f5f5' : '#fff'
                }}
                secondaryAction={
                    !isEditing && (
                        <Stack direction="row" spacing={1}>
                            <Tooltip title="Lägg till flera underkategorier">
                                <IconButton 
                                    size="small" 
                                    onClick={() => setBulkAddOpen(category._id)}
                                >
                                    <AddIcon />
                                </IconButton>
                            </Tooltip>
                            <IconButton size="small" onClick={() => startEdit(category)}>
                                <EditIcon />
                            </IconButton>
                            <IconButton 
                                size="small" 
                                onClick={() => handleDeleteCategory(category._id, category.name)} 
                                disabled={deletingCategoryId === category._id}
                            >
                                {deletingCategoryId === category._id ? 
                                    <CircularProgress size={16} /> : 
                                    <DeleteIcon />
                                }
                            </IconButton>
                        </Stack>
                    )
                }
            >
                <DragHandleIcon sx={{ mr: 1, color: '#999', cursor: 'grab' }} />
                {hasSubcategories && (
                    <IconButton size="small" onClick={() => toggleExpand(category._id)}>
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                )}
                
                {isEditing ? (
                    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                            size="small"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            sx={{ flexGrow: 1 }}
                        />
                        <IconButton 
                            size="small" 
                            onClick={saveEdit}
                            disabled={!editName.trim() || savingEdit}
                            color="primary"
                        >
                            {savingEdit ? <CircularProgress size={16} /> : <SaveIcon />}
                        </IconButton>
                        <IconButton 
                            size="small" 
                            onClick={cancelEdit}
                            disabled={savingEdit}
                        >
                            <CancelIcon />
                        </IconButton>
                    </Box>
                ) : (
                    <ListItemText
                        primary={category.name}
                        secondary={category.description || undefined}
                        sx={{ ml: hasSubcategories ? 0 : 5 }}
                    />
                )}
            </ListItem>

            {/* Subcategories with drag-and-drop */}
            {hasSubcategories && (
                <Collapse in={isExpanded} sx={{ backgroundColor: isMainCategory ? '#f5f5f5' : 'transparent' }}>
                    <Box sx={{ pl: 2, pr: 2, pb: 1 }}>
                        <DndContext 
                            sensors={sensors} 
                            collisionDetection={closestCenter}
                            onDragEnd={async (event: any) => {
                                const { active, over } = event;
                                if (!over || active.id === over.id) return;
                                const ids = subcategories.map(s => s._id);
                                const oldIndex = ids.indexOf(active.id);
                                const newIndex = ids.indexOf(over.id);
                                if (oldIndex === -1 || newIndex === -1) return;
                                const newOrderIds = arrayMove(ids, oldIndex, newIndex);
                                try {
                                    await reorderWithinParent(category._id, newOrderIds);
                                    await handleCategoryUpdated();
                                } catch (err) {
                                    console.error('Subcategory reorder failed', err);
                                }
                            }}
                        >
                            <SortableContext items={subcategories.map(s => s._id)} strategy={verticalListSortingStrategy}>
                                {subcategories.map(subcat => (
                                    <SortableSubItem key={subcat._id} id={subcat._id}>
                                        <CategoryListItem
                                            category={subcat}
                                            getSubcategories={getSubcategories}
                                            expandedCategories={expandedCategories}
                                            editingCategory={editingCategory}
                                            editName={editName}
                                            savingEdit={savingEdit}
                                            deletingCategoryId={deletingCategoryId}
                                            mainCategories={mainCategories}
                                            sensors={sensors}
                                            setBulkAddOpen={setBulkAddOpen}
                                            toggleExpand={toggleExpand}
                                            startEdit={startEdit}
                                            setEditName={setEditName}
                                            saveEdit={saveEdit}
                                            cancelEdit={cancelEdit}
                                            handleDeleteCategory={handleDeleteCategory}
                                            handleCategoryUpdated={handleCategoryUpdated}
                                            reorderWithinParent={reorderWithinParent}
                                        />
                                    </SortableSubItem>
                                ))}
                            </SortableContext>
                        </DndContext>
                    </Box>
                </Collapse>
            )}
        </Paper>
    );

    // Wrap main categories with SortableMainItem for DnD
    if (isMainCategory) {
        return (
            <SortableMainItem id={category._id}>
                {content}
            </SortableMainItem>
        );
    }
    return content;
};

const CategoryManagementPage: React.FC<CategoryManagementPageProps> = ({
    categories,
    loading,
    error,
    onCategoryUpdated,
}) => {
    // Wrapper to notify App when categories are updated
    const handleCategoryUpdated = useCallback(async () => {
        await onCategoryUpdated();
        notifyCategoriesUpdated();
    }, [onCategoryUpdated]);
    
    const [deletingCategoryId, setDeletingCategoryId] = useState<string | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
    const [bulkAddOpen, setBulkAddOpen] = useState<string | null>(null);
    const [categoryChips, setCategoryChips] = useState<string[]>([]);
    const [addingBulkCategories, setAddingBulkCategories] = useState(false);
    const [editingCategory, setEditingCategory] = useState<string | null>(null);
    const [editName, setEditName] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [savingEdit, setSavingEdit] = useState(false);
    const [browseSettings, setBrowseSettings] = useState<CategoryBrowseSettings>({
        hideEmptyCategoriesEnabled: false,
        updatedAt: null
    });
    const [loadingBrowseSettings, setLoadingBrowseSettings] = useState(true);
    const [savingBrowseSettings, setSavingBrowseSettings] = useState(false);
    
    // New states for main category creation
    const [addMainCategoryOpen, setAddMainCategoryOpen] = useState(false);
    const [, setAddingMainCategory] = useState(false);
    const newMainNameRef = useRef<HTMLInputElement>(null);
    
    // Snackbar state for success/error messages
    const [snackbar, setSnackbar] = useState<{open: boolean; message: string; severity: 'success' | 'error' | 'info'}>({
        open: false,
        message: '',
        severity: 'success'
    });
    
    // Delete confirmation dialog state
    const [deleteDialog, setDeleteDialog] = useState<{open: boolean; categoryId: string; categoryName: string}>({
        open: false,
        categoryId: '',
        categoryName: ''
    });
    
    const showSnackbar = useCallback((message: string, severity: 'success' | 'error' | 'info' = 'success') => {
        setSnackbar({ open: true, message, severity });
    }, []);
    
    const handleCloseSnackbar = useCallback(() => {
        setSnackbar(prev => ({ ...prev, open: false }));
    }, []);

    const loadBrowseSettings = useCallback(async () => {
        try {
            setLoadingBrowseSettings(true);
            const { data } = await axiosInstance.get('/api/categories/browse-settings');
            setBrowseSettings({
                hideEmptyCategoriesEnabled: !!data?.settings?.hideEmptyCategoriesEnabled,
                updatedAt: data?.settings?.updatedAt ? String(data.settings.updatedAt) : null,
            });
        } catch (err: any) {
            console.error('Error loading category browse settings:', err.response?.data || err);
            showSnackbar('Kunde inte läsa inställningen för huvudgriddens kategorier.', 'error');
        } finally {
            setLoadingBrowseSettings(false);
        }
    }, [showSnackbar]);

    useEffect(() => {
        void loadBrowseSettings();
    }, [loadBrowseSettings]);

    const handleBrowseSettingToggle = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextValue = event.target.checked;
        const previousSettings = browseSettings;

        setBrowseSettings(prev => ({
            ...prev,
            hideEmptyCategoriesEnabled: nextValue,
        }));

        try {
            setSavingBrowseSettings(true);
            const { data } = await axiosInstance.put('/api/categories/browse-settings', {
                hideEmptyCategoriesEnabled: nextValue,
            });
            setBrowseSettings({
                hideEmptyCategoriesEnabled: !!data?.settings?.hideEmptyCategoriesEnabled,
                updatedAt: data?.settings?.updatedAt ? String(data.settings.updatedAt) : null,
            });
            notifyCategoriesUpdated();
            showSnackbar('Kategoriinställningen för huvudgridden sparades.', 'success');
        } catch (err: any) {
            console.error('Error saving category browse settings:', err.response?.data || err);
            setBrowseSettings(previousSettings);
            showSnackbar(err.response?.data?.message || err.message || 'Kunde inte spara kategoriinställningen.', 'error');
        } finally {
            setSavingBrowseSettings(false);
        }
    }, [browseSettings, showSnackbar]);

    // Filter main categories (those without a parent)
    const mainCategories = categories
        .filter(cat => !cat.parentCategory)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name));

    // Local order state for smooth DnD preview
    const [localMainOrder, setLocalMainOrder] = useState<string[] | null>(null);
    const mainIds = (localMainOrder ?? mainCategories.map(c => c._id));

    // dnd-kit sensors and handlers
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

    const handleDragEnd = useCallback(async (event: any) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const items = mainIds;
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        if (oldIndex === -1 || newIndex === -1) return;
        const newOrder = arrayMove(items, oldIndex, newIndex);
        setLocalMainOrder(newOrder);
        try {
            const payload = newOrder.map((id, idx) => ({ id, order: (idx + 1) * 10 }));
            await axios.post(`${API_BASE_URL}/api/categories/reorder`, { parentId: null, items: payload });
            await handleCategoryUpdated();
            setLocalMainOrder(null);
        } catch (err) {
            console.error('Reorder failed', err);
        }
    }, [mainIds, handleCategoryUpdated]);

    // Get subcategories for a given parent category id
    const getSubcategories = (parentId: string) => {
        return categories.filter(cat => {
            if (typeof cat.parentCategory === 'string') {
                return cat.parentCategory === parentId;
            } else if (cat.parentCategory && typeof cat.parentCategory === 'object') {
                return (cat.parentCategory as {_id: string})._id === parentId;
            }
            return false;
        });
    };

    // Check if this category has subcategories
    const hasSubcategories = (categoryId: string) => {
        return categories.some(cat => {
            if (typeof cat.parentCategory === 'string') {
                return cat.parentCategory === categoryId;
            } else if (cat.parentCategory && typeof cat.parentCategory === 'object') {
                return (cat.parentCategory as {_id: string})._id === categoryId;
            }
            return false;
        });
    }

    const toggleExpand = (categoryId: string) => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryId]: !prev[categoryId]
        }));
    };


    // Bulk add subcategories
    const handleBulkAdd = async (parentId: string) => {
        if (categoryChips.length === 0) return;

        setAddingBulkCategories(true);
        let successCount = 0;
        let errorCount = 0;
        const errors: string[] = [];

        try {
            console.log(`Starting bulk add of ${categoryChips.length} categories to parent ${parentId}`);
            
            // Add categories one by one with better error handling
            for (let i = 0; i < categoryChips.length; i++) {
                const name = categoryChips[i];
                console.log(`Adding category ${i + 1}/${categoryChips.length}: "${name}"`);
                
                try {
                    // Check if category already exists first
                    const existingCategory = categories.find(cat => 
                        cat.name.toLowerCase().trim() === name.toLowerCase().trim() && 
                        cat.parentCategory === parentId
                    );
                    
                    if (existingCategory) {
                        console.log(`Category "${name}" already exists, skipping`);
                        continue;
                    }

                    const response = await axios.post(`${API_BASE_URL}/api/categories`, {
                        name: name.trim(),
                        description: '',
                        parentCategory: parentId
                    });
                    
                    console.log(`Successfully created category "${name}":`, response.data);
                    successCount++;
                    
                    // Small delay to prevent overwhelming the server
                    await new Promise(resolve => setTimeout(resolve, 100));
                    
                } catch (categoryError: any) {
                    console.error(`Failed to create category "${name}":`, categoryError);
                    errorCount++;
                    errors.push(`${name}: ${categoryError.response?.data?.message || categoryError.message}`);
                }
            }
            
            console.log(`Bulk add completed. Success: ${successCount}, Errors: ${errorCount}`);
            
            // Always refresh categories to see what was actually created
            await handleCategoryUpdated();
            
            // Show results to user
            if (errorCount > 0) {
                showSnackbar(`${successCount} kategorier skapade, ${errorCount} fel`, 'error');
            } else if (successCount > 0) {
                showSnackbar(`${successCount} ${successCount === 1 ? 'kategori' : 'kategorier'} skapades framgångsrikt!`, 'success');
            }
            
            setCategoryChips([]);
            setBulkAddOpen(null);
            
            // Auto-expand the parent category to show new subcategories
            setExpandedCategories(prev => ({
                ...prev,
                [parentId]: true
            }));
            
        } catch (err: any) {
            console.error('Error in bulk add operation:', err);
            showSnackbar(`Ett oväntat fel inträffade: ${err.message}`, 'error');
        } finally {
            setAddingBulkCategories(false);
        }
    };

    // Apply category template
    const applyTemplate = async (parentId: string, template: string[]) => {
        setAddingBulkCategories(true);
        
        try {
            for (const name of template) {
                // Check if category already exists
                const existingCategory = categories.find(cat => 
                    cat.name.toLowerCase() === name.toLowerCase() && 
                    cat.parentCategory === parentId
                );
                
                if (!existingCategory) {
                    await axios.post(`${API_BASE_URL}/api/categories`, {
                        name,
                        description: '',
                        parentCategory: parentId
                    });
                }
            }
            
            setBulkAddOpen(null);
            await handleCategoryUpdated();
            
            // Auto-expand the parent category
            setExpandedCategories(prev => ({
                ...prev,
                [parentId]: true
            }));
        } catch (err: any) {
            console.error('Error applying template:', err);
        } finally {
            setAddingBulkCategories(false);
        }
    };

    // Apply template to chips
    const applyTemplateToChips = (template: string[]) => {
        const newChips = template.filter(item => !categoryChips.includes(item));
        setCategoryChips(prev => [...prev, ...newChips]);
    };

    // Inline editing
    const startEdit = (category: ICategory) => {
        setEditingCategory(category._id);
        setEditName(category.name);
        setEditDescription(category.description || '');
    };

    const cancelEdit = () => {
        setEditingCategory(null);
        setEditName('');
        setEditDescription('');
    };

    const saveEdit = async () => {
        if (!editingCategory || !editName.trim()) return;

        setSavingEdit(true);
        try {
            await axios.put(`${API_BASE_URL}/api/categories/${editingCategory}`, {
                name: editName,
                description: editDescription
            });
            
            setEditingCategory(null);
            await handleCategoryUpdated();
            showSnackbar('Kategori uppdaterad', 'success');
        } catch (err: any) {
            console.error('Error saving category:', err);
        } finally {
            setSavingEdit(false);
        }
    };

    const handleDeleteCategory = (categoryId: string, categoryName: string) => {
        // Check if this category has subcategories
        if (hasSubcategories(categoryId)) {
            showSnackbar('Denna kategori har underkategorier. Ta bort underkategorierna först.', 'error');
            return;
        }

        // Open delete confirmation dialog
        setDeleteDialog({
            open: true,
            categoryId,
            categoryName
        });
    };
    
    const confirmDelete = async () => {
        const { categoryId } = deleteDialog;
        setDeleteDialog({ open: false, categoryId: '', categoryName: '' });
        
        setDeletingCategoryId(categoryId);
        setDeleteError(null);
        try {
            await axios.delete(`${API_BASE_URL}/api/categories/${categoryId}`);
            await handleCategoryUpdated();
            showSnackbar('Kategori raderad', 'success');
        } catch (err: any) {
            setDeleteError(`Misslyckades att radera kategori: ${err.message}`);
        } finally {
            setDeletingCategoryId(null);
        }
    };
    
    const cancelDelete = () => {
        setDeleteDialog({ open: false, categoryId: '', categoryName: '' });
    };

    // Get template suggestions based on category name
    const getTemplateSuggestions = (categoryName: string) => {
        const name = categoryName.toLowerCase();
        if (name.includes('produkt')) return CATEGORY_TEMPLATES.produkter;
        if (name.includes('mat')) return CATEGORY_TEMPLATES.mat;
        if (name.includes('butik') || name.includes('material')) return CATEGORY_TEMPLATES.butik;
        if (name.includes('media') || name.includes('bild')) return CATEGORY_TEMPLATES.media;
        if (name.includes('social') || name.includes('some')) return CATEGORY_TEMPLATES.social;
        return null;
    };

    // Component for main category dialog
    const AddMainCategoryDialog = () => (
        <Dialog 
            open={addMainCategoryOpen} 
            onClose={() => setAddMainCategoryOpen(false)} 
            maxWidth="sm" 
            fullWidth
            keepMounted
            disableEnforceFocus
            disableAutoFocus
            disableRestoreFocus
        >
            <DialogTitle id="add-main-category-title">
                Lägg till huvudkategori
            </DialogTitle>
            <AddMainCategoryInnerForm onClose={() => setAddMainCategoryOpen(false)} />
        </Dialog>
    );

    // Component for bulk add dialog with completely isolated input
    const BulkAddDialog = ({ parentId, open, onClose }: { parentId: string; open: boolean; onClose: () => void }) => {
        const parentCategory = categories.find(cat => cat._id === parentId);
        const suggestions = parentCategory ? getTemplateSuggestions(parentCategory.name) : null;

        return (
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    Lägg till underkategorier till: {parentCategory?.name}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Skriv kategorinamn och tryck <strong>Enter</strong> för att skapa varje kategori.
                    </Typography>
                    
                    <IsolatedChipInput
                        chips={categoryChips}
                        onChipsChange={setCategoryChips}
                        placeholder="Skriv kategorinamn och tryck Enter..."
                    />
                    
                    {categoryChips.length > 0 && (
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                            {categoryChips.length} kategorier redo att läggas till
                        </Typography>
                    )}

                    {suggestions && (
                        <Paper sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5' }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Föreslagna mallar:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => applyTemplate(parentId, suggestions)}
                                    disabled={addingBulkCategories}
                                    startIcon={<ContentCopyIcon />}
                                >
                                    Skapa direkt ({suggestions.length} st)
                                </Button>
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => applyTemplateToChips(suggestions)}
                                    startIcon={<AddIcon />}
                                >
                                    Lägg till i formuläret
                                </Button>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {suggestions.map((item, index) => (
                                    <Chip 
                                        key={index} 
                                        label={item} 
                                        size="small" 
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        </Paper>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>
                        Avbryt
                    </Button>
                    <Button 
                        onClick={() => handleBulkAdd(parentId)}
                        disabled={categoryChips.length === 0 || addingBulkCategories}
                        variant="contained"
                        startIcon={addingBulkCategories ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                    >
                        {addingBulkCategories ? 'Lägger till...' : `Lägg till ${categoryChips.length} kategorier`}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    // Reset chips when dialog closes
    useEffect(() => {
        if (!bulkAddOpen) {
            setCategoryChips([]);
        }
    }, [bulkAddOpen]);

    // Reset main category form when dialog closes / focus the input when opens
    useEffect(() => {
        if (addMainCategoryOpen) {
            setTimeout(() => newMainNameRef.current?.focus(), 0);
        }
    }, [addMainCategoryOpen]);

  // Isolated submit function used by inner form
  const submitMainCategory = async (name: string, description: string) => {
      if (!name.trim()) return;
      setAddingMainCategory(true);
      try {
          await axios.post(`${API_BASE_URL}/api/categories`, {
              name: name.trim(),
              description: description,
              parentCategory: null
          });
          await handleCategoryUpdated();
      } catch (err: any) {
          console.error('Error adding main category:', err);
          const backendMsg = err?.response?.data?.message || '';
          const validationErrors = err?.response?.data?.errors as Array<{ field: string; message: string }> | undefined;
          let friendly = backendMsg;
          if (validationErrors && validationErrors.length > 0) {
              friendly = validationErrors.map(e => `${e.field}: ${e.message}`).join(', ');
          }
          if (!friendly) friendly = 'Kunde inte skapa kategori. Kontrollera fälten och försök igen.';
          showSnackbar(friendly, 'error');
          throw err;
      } finally {
          setAddingMainCategory(false);
      }
  };

  // Inner isolated form component (memoized) to avoid parent focus interference
  const AddMainCategoryInnerForm = React.memo(({ onClose }: { onClose: () => void }) => {
      const [name, setName] = useState('');
      const [description, setDescription] = useState('');
      const [submitting, setSubmitting] = useState(false);
      const localRef = useRef<HTMLInputElement>(null);

      useEffect(() => {
          // Focus once when mounted
          setTimeout(() => localRef.current?.focus(), 0);
      }, []);

      const handleSubmit = async (e: React.FormEvent) => {
          e.preventDefault();
          if (!name.trim() || submitting) return;
          setSubmitting(true);
          try {
              await submitMainCategory(name, description);
              onClose();
          } finally {
              setSubmitting(false);
          }
      };

      return (
          <Box component="form" aria-labelledby="add-main-category-title" onSubmit={handleSubmit}>
              <DialogContent>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      Skapa en ny huvudkategori som kan innehålla underkategorier.
                  </Typography>

                  <TextField
                      margin="dense"
                      label="Kategorinamn"
                      fullWidth
                      variant="outlined"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      inputRef={localRef}
                      sx={{ mb: 2 }}
                      required
                      id="mainCategoryName"
                      name="name"
                      autoComplete="off"
                      onKeyDown={(e) => e.stopPropagation()}
                  />

                  <TextField
                      margin="dense"
                      label="Beskrivning (valfritt)"
                      fullWidth
                      multiline
                      rows={2}
                      variant="outlined"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      id="mainCategoryDescription"
                      name="description"
                      autoComplete="off"
                      onKeyDown={(e) => e.stopPropagation()}
                  />
              </DialogContent>
              <DialogActions>
                  <Button onClick={onClose}>Avbryt</Button>
                  <Button 
                      type="submit"
                      disabled={!name.trim() || submitting}
                      variant="contained"
                      startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <CategoryIcon />}
                  >
                      {submitting ? 'Skapar...' : 'Skapa huvudkategori'}
                  </Button>
              </DialogActions>
          </Box>
      );
  });

    // Helper to reorder categories inside a parent
    const reorderWithinParent = useCallback(async (parentId: string | null, orderedIds: string[]) => {
        const payload = orderedIds.map((id, idx) => ({ id, order: (idx + 1) * 10 }));
        await axios.post(`${API_BASE_URL}/api/categories/reorder`, { parentId, items: payload });
    }, []);

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" component="h2">
                    Hantera Kategorier
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => setAddMainCategoryOpen(true)}
                    startIcon={<CategoryIcon />}
                >
                    Ny huvudkategori
                </Button>
            </Box>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                    Visning i huvudgridden
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Admin ser alltid alla kategorier. Vanliga användare ser bara kategorier med innehåll. Kontor och Kontor Plus ser bara kategorier med kontorsrelevant innehåll.
                </Typography>

                {loadingBrowseSettings ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={20} />
                        <Typography variant="body2" color="text.secondary">
                            Läser inställning...
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={browseSettings.hideEmptyCategoriesEnabled}
                                    onChange={handleBrowseSettingToggle}
                                    disabled={savingBrowseSettings}
                                />
                            }
                            label="Dölj tomma kategorier i huvudgridden"
                        />
                        <Typography variant="caption" color="text.secondary">
                            När switchen är av visas alla kategorier. När den är på används rollstyrd filtrering för browse-vyn.
                        </Typography>
                        {savingBrowseSettings && (
                            <Typography variant="caption" color="text.secondary">
                                Sparar...
                            </Typography>
                        )}
                    </Box>
                )}
            </Paper>

            <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Tips:</strong> Skapa huvudkategorier med "Ny huvudkategori"-knappen och använd ➕ för att lägga till flera underkategorier samtidigt!
            </Alert>

            {loading ? (
                <CircularProgress size={20} />
            ) : error ? (
                <Typography color="error">Kunde inte ladda kategorier: {error}</Typography>
            ) : categories.length === 0 ? (
                <Typography>Inga kategorier funna. Skapa din första huvudkategori!</Typography>
            ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={mainIds} strategy={verticalListSortingStrategy}>
                    <List sx={{ cursor: 'grab' }}>
                      {mainIds.map(id => {
                        const cat = mainCategories.find(c => c._id === id);
                        if (!cat) return null;
                        return (
                          <CategoryListItem
                            key={cat._id}
                            category={cat}
                            getSubcategories={getSubcategories}
                            expandedCategories={expandedCategories}
                            editingCategory={editingCategory}
                            editName={editName}
                            savingEdit={savingEdit}
                            deletingCategoryId={deletingCategoryId}
                            mainCategories={mainCategories}
                            sensors={sensors}
                            setBulkAddOpen={setBulkAddOpen}
                            toggleExpand={toggleExpand}
                            startEdit={startEdit}
                            setEditName={setEditName}
                            saveEdit={saveEdit}
                            cancelEdit={cancelEdit}
                            handleDeleteCategory={handleDeleteCategory}
                            handleCategoryUpdated={handleCategoryUpdated}
                            reorderWithinParent={reorderWithinParent}
                          />
                        );
                      })}
                    </List>
                  </SortableContext>
                </DndContext>
            )}
            
            {deleteError && (
                <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>
            )}

            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                    Tips: Dra och släpp huvudkategorier för att ändra ordning
                </Typography>
            </Box>

            <AddMainCategoryDialog />

            {bulkAddOpen && (
                <BulkAddDialog
                    parentId={bulkAddOpen}
                    open={!!bulkAddOpen}
                    onClose={() => setBulkAddOpen(null)}
                />
            )}
            
            {/* Delete confirmation dialog */}
            <Dialog
                open={deleteDialog.open}
                onClose={cancelDelete}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Radera kategori?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Är du säker på att du vill radera kategorin <strong>"{deleteDialog.categoryName}"</strong>?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Detta kan påverka assets som är tilldelade denna kategori.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelDelete}>
                        Avbryt
                    </Button>
                    <Button 
                        onClick={confirmDelete} 
                        color="error" 
                        variant="contained"
                    >
                        Radera
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Snackbar for success/error messages */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CategoryManagementPage;

